import React, { useState, useEffect } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TekyuLogo from './TekyuLogo';
import { DataStore } from '@aws-amplify/datastore';
import { loadSavedRoadmaps } from './utils/saveRoadmap';
import { UserSubscriptionPlan, Progress, RoadmapRun, ProjectPlaceholder, Roadmap } from './models';
import RoadmapTimeline from './components/RoadmapTimeline';
import RoadmapTracker from './components/RoadmapTracker';
import ProRoadmapGenerator from './components/ProRoadmapGenerator';
import ProjectsTab from './components/ProjectsTab';
import UpsellBanner from './components/UpsellBanner';
import BootcampCTA from './components/BootcampCTA';
import {
  BookOpen,
  CheckCircle2,
  Clock,
  FileText,
  FolderKanban,
  Github,
  Loader2,
  Plus,
  Sparkles,
  Target,
  TrendingUp,
  Zap,
  User,
} from 'lucide-react';

const ROLE_NAMES = {
  'devsecops': 'DevSecOps Engineer',
  'devex': 'Developer Experience Engineer',
  'cloud-platform-engineer': 'Cloud Platform Engineer',
  'devops-questionnaire': 'DevOps Engineer',
  'sre-questionnaire': 'Site Reliability Engineer',
  'penetration-testing': 'Penetration Tester',
  'cybergrc': 'Cyber GRC Analyst',
  'product-manager': 'Product Manager',
  'ai-data-engineer': 'AI Data Engineer',
  'security-analyst': 'Security Analyst',
  'full-stack-engineer': 'Full Stack Engineer',
  'devsecai': 'DevSecAI Engineer',
};

function DashboardContent({ user, signOut }) {
  const navigate = useNavigate();
  const [isPro, setIsPro] = useState(false);
  const [loading, setLoading] = useState(true);
  const [roadmapRuns, setRoadmapRuns] = useState([]);
  const [roadmapError, setRoadmapError] = useState('');
  const [selectedRoadmap, setSelectedRoadmap] = useState(null);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('dashboard');
  useEffect(() => { const tab=searchParams.get('tab'); if(['dashboard','roadmaps','projects'].includes(tab)) setActiveTab(tab); },[searchParams]);
  const [projects, setProjects] = useState([]);
  const [progress, setProgress] = useState({});
  const [showProGenerator, setShowProGenerator] = useState(false);

  useEffect(() => {
    // Add overall timeout to ensure loading state is cleared
    const loadDataWithTimeout = async () => {
      const timeout = setTimeout(() => {
        console.warn('loadData timeout - forcing loading to false');
        setLoading(false);
      }, 15000); // 15 second overall timeout
      
      try {
        // Wait for DataStore to be ready before querying
        await new Promise(resolve => setTimeout(resolve, 2000));
        await loadData();
      } finally {
        clearTimeout(timeout);
      }
    };
    
    loadDataWithTimeout();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Learning access requires a signed-in account, not a paid subscription.
      const { checkLearningAccess } = await import('./utils/learningAccess');
      const isProStatus = await checkLearningAccess();
      setIsPro(isProStatus);
      
      // Try DataStore queries with short timeout (non-blocking)
      const queryTimeout = (promise, timeoutMs = 2000) => {
        return Promise.race([
          promise,
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Query timeout')), timeoutMs)
          )
        ]);
      };
      
      // Try to load additional data from DataStore (non-critical)
      // Don't wait for DataStore if it's slow - subscription status is already set
      Promise.all([
        queryTimeout(
          DataStore.query(UserSubscriptionPlan).catch((err) => {
            console.warn('Error querying UserSubscriptionPlan:', err);
            return [];
          })
        ).catch(() => []),
        queryTimeout(
          DataStore.query(Progress).then(rows => rows.filter(p => !p.id.includes('::project::'))).catch((err) => {
            console.warn('Error querying Progress:', err);
            return [];
          })
        ).catch(() => []),
      ]).then(([plans, prog]) => {
        if (prog.length > 0 && prog[0]?.data) {
          setProgress(typeof prog[0].data === 'string' ? JSON.parse(prog[0].data) : prog[0].data);
        }
      }).catch(() => {
        // Ignore errors - subscription status already set
      });

      setRoadmapError('');
      try {
        setRoadmapRuns(await loadSavedRoadmaps());
      } catch (error) {
        setRoadmapError(error.message);
      }

      try {
        if (ProjectPlaceholder) {
          const projs = await queryTimeout(
            DataStore.query(ProjectPlaceholder).catch((err) => {
              console.warn('Error querying ProjectPlaceholder:', err);
              return [];
            })
          ).catch(() => []);
          setProjects(projs || []);
        } else {
          setProjects([]);
        }
      } catch (e) {
        console.warn('ProjectPlaceholder not available:', e);
        setProjects([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      // Always set loading to false, even if there were errors
      setLoading(false);
    }
  };

  const handleStripeSuccess = async () => {
    await loadData();
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-green-400" />
        <p className="text-slate-400 text-sm">Loading your dashboard...</p>
        <p className="text-slate-500 text-xs">This may take a few seconds</p>
      </div>
    );
  }

  if (showProGenerator) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <ProRoadmapGenerator
          roleKey="custom"
          onRoadmapGenerated={(roadmap) => {
            // After generating, save it and reload
            loadData();
            setShowProGenerator(false);
          }}
        />
      </div>
    );
  }

  if (selectedRoadmap) {
    return (
      <RoadmapDetailView
        roadmapRun={selectedRoadmap}
        onBack={() => setSelectedRoadmap(null)}
        isPro={isPro}
        progress={progress}
        projects={projects.filter(p => p.roadmapRun?.id === selectedRoadmap.id)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      {/* Ambient gradient overlay */}
      <div className="fixed inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-green-400/5 pointer-events-none" />
      
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <TekyuLogo className="h-10 w-auto" ariaLabel="Tekyu Logo" />
            <div>
              <div className="text-2xl font-extrabold bg-gradient-to-r from-green-400 to-green-300 bg-clip-text text-transparent">
                Tekyu Portal
              </div>
              <div className="text-sm text-slate-400">
                {user?.signInDetails?.loginId || user?.username}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="rounded-xl bg-slate-800/50 border border-slate-700 px-4 py-2 text-sm font-bold hover:bg-slate-800 transition-colors flex items-center gap-2"
            >
              <User className="h-4 w-4" />
              Profile
            </button>
            <button
              type="button"
              onClick={signOut}
              className="rounded-xl bg-slate-800/50 border border-slate-700 px-4 py-2 text-sm font-bold hover:bg-slate-800 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>

        {roadmapError && <div role="alert" className="mb-5 rounded-xl border border-amber-600 p-4 text-amber-200">
          {roadmapError} <button type="button" onClick={loadData} className="ml-3 underline">Retry</button>
        </div>}
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-800">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: Target },
            { id: 'roadmaps', label: 'Roadmaps', icon: BookOpen },
            { id: 'projects', label: 'Projects', icon: FolderKanban },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-3 font-bold text-sm transition-colors border-b-2 ${
                activeTab === id
                  ? 'border-green-400 text-green-400'
                  : 'border-transparent text-slate-400 hover:text-slate-300'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/60 to-slate-900/40 p-6 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="rounded-xl bg-green-400/10 p-2 border border-green-400/20">
                    <BookOpen className="h-5 w-5 text-green-400" />
                  </div>
                  <div className="text-2xl font-extrabold text-white">{roadmapRuns.length}</div>
                </div>
                <div className="text-sm text-slate-400">Saved Roadmaps</div>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/60 to-slate-900/40 p-6 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="rounded-xl bg-blue-400/10 p-2 border border-blue-400/20">
                    <FolderKanban className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="text-2xl font-extrabold text-white">{projects.length}</div>
                </div>
                <div className="text-sm text-slate-400">Projects</div>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/60 to-slate-900/40 p-6 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="rounded-xl bg-yellow-400/10 p-2 border border-yellow-400/20">
                    <TrendingUp className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="text-2xl font-extrabold text-white">
                    {Object.keys(progress).filter(k => progress[k] === true).length}
                  </div>
                </div>
                <div className="text-sm text-slate-400">Completed Items</div>
              </div>
            </div>

            {/* Subscription Status */}
            {isPro && (
              <div className="rounded-2xl border border-green-500/30 bg-gradient-to-br from-green-500/10 to-green-400/5 p-6 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-green-400/20 p-2 border border-green-400/30">
                    <Sparkles className="h-6 w-6 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-lg font-extrabold text-green-300">Free learning workspace</div>
                    <div className="text-sm text-slate-300 mt-1">Save your roadmaps and work through guided projects with your free account.</div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/60 to-slate-900/40 p-6 backdrop-blur-sm">
              <div className="text-lg font-extrabold text-white mb-4">Quick Actions</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {isPro ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowProGenerator(true)}
                      className="rounded-xl border border-green-400/30 bg-green-400/10 px-4 py-3 text-sm font-bold text-green-300 hover:bg-green-400/20 transition-colors flex items-center gap-2"
                    >
                      <Sparkles className="h-4 w-4" />
                      Roadmap Builder
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard')}
                      className="rounded-xl border border-slate-800 bg-slate-950/30 px-4 py-3 text-sm font-bold hover:bg-slate-950/60 transition-colors flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Standard Generator
                    </button>
                  
                    <button
                      type="button"
                      onClick={() => navigate('/cv-generator')}
                      className="rounded-xl border border-slate-800 bg-slate-950/30 px-4 py-3 text-sm font-bold hover:bg-slate-950/60 transition-colors flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      CV Generator
                    </button>
</>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="rounded-xl border border-slate-800 bg-slate-950/30 px-4 py-3 text-sm font-bold hover:bg-slate-950/60 transition-colors flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Generate New Roadmap
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setActiveTab('roadmaps')}
                  className="rounded-xl border border-slate-800 bg-slate-950/30 px-4 py-3 text-sm font-bold hover:bg-slate-950/60 transition-colors flex items-center gap-2"
                >
                  <BookOpen className="h-4 w-4" />
                  View All Roadmaps
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('projects')}
                  className="rounded-xl border border-slate-800 bg-slate-950/30 px-4 py-3 text-sm font-bold hover:bg-slate-950/60 transition-colors flex items-center gap-2"
                >
                  <FolderKanban className="h-4 w-4" />
                  View Projects
                </button>
              </div>

              {/* Bootcamp CTA */}
              <BootcampCTA variant="default" context="portal" />
            </div>

            {/* Upsell for non-Pro users */}
            {!isPro && (
              <UpsellBanner variant="feature-card" />
            )}
          </div>
        )}

        {/* Roadmaps Tab */}
        {activeTab === 'roadmaps' && (
          <div className="space-y-4">
            {/* Upsell banner for non-Pro users */}
            {!isPro && (
              <UpsellBanner variant="inline" />
            )}
            {roadmapRuns.length === 0 ? (
              <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-12 text-center">
                <BookOpen className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <div className="text-lg font-bold text-slate-300 mb-2">No saved roadmaps yet</div>
                <div className="text-sm text-slate-500 mb-6">Generate a roadmap to get started.</div>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="rounded-xl bg-green-400 px-6 py-3 font-bold text-slate-950 hover:bg-green-300 transition-colors"
                >
                  Create Roadmap
                </button>
              </div>
            ) : (
              roadmapRuns.map((run) => (
                <div
                  key={run.id}
                  className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/60 to-slate-900/40 p-6 backdrop-blur-sm hover:border-slate-700 transition-colors cursor-pointer"
                  onClick={() => setSelectedRoadmap(run)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="text-xl font-extrabold text-white mb-1">{run.title}</div>
                      <div className="text-sm text-slate-400 mb-3">{run.subtitle || ROLE_NAMES[run.roleKey] || run.roleKey}</div>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {run.totalMonths} months
                        </div>
                        {run.createdAt && (
                          <div>{new Date(run.createdAt).toLocaleDateString()}</div>
                        )}
                      </div>
                    </div>
                    <div className="rounded-xl bg-green-400/10 border border-green-400/20 px-3 py-1 text-xs font-bold text-green-300">
                      View
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <ProjectsTab isPro={isPro} />
        )}
      </div>
    </div>
  );
}

function RoadmapDetailView({ roadmapRun, onBack, isPro, progress, projects }) {
  return (
    <RoadmapTracker 
      roadmapRun={roadmapRun} 
      isPro={isPro} 
      onBack={onBack}
    />
  );
}

export default function PortalPage() {
  return (
    <div className="min-h-screen app-shell app-ambient text-white">
      <div className="app-content mx-auto max-w-7xl px-4 py-10">
        <Authenticator
          components={{
            Header() {
              return (
                <div className="flex flex-col items-center pt-8 pb-4 px-8">
                  <TekyuLogo className="h-14 w-auto mb-4" ariaLabel="Tekyu Logo" />
                  <h1 className="text-2xl font-extrabold bg-gradient-to-r from-green-400 to-green-300 bg-clip-text text-transparent">
                    Welcome to Tekyu
                  </h1>
                  <p className="text-slate-400 text-sm mt-2 text-center">
                    Your cybersecurity career starts here
                  </p>
                </div>
              );
            },
            SignIn: {
              Header() {
                return null; // Hide default header, we use the main Header above
              },
              Footer() {
                return (
                  <div className="text-center pb-4 px-8">
                    <p className="text-slate-500 text-xs">
                      By signing in, you agree to our{' '}
                      <a href="/terms" className="text-green-400 hover:text-green-300">Terms of Service</a>
                      {' '}and{' '}
                      <a href="/privacy" className="text-green-400 hover:text-green-300">Privacy Policy</a>
                    </p>
                  </div>
                );
              },
            },
            SignUp: {
              Header() {
                return null; // Hide default header
              },
              Footer() {
                return (
                  <div className="text-center pb-4 px-8">
                    <p className="text-slate-500 text-xs">
                      By creating an account, you agree to our{' '}
                      <a href="/terms" className="text-green-400 hover:text-green-300">Terms of Service</a>
                      {' '}and{' '}
                      <a href="/privacy" className="text-green-400 hover:text-green-300">Privacy Policy</a>
                    </p>
                  </div>
                );
              },
            },
            ConfirmSignUp: {
              Header() {
                return (
                  <div className="text-center pt-6 pb-2 px-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-green-400/10 border border-green-400/20 mb-4">
                      <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-bold text-white">Check your email</h2>
                    <p className="text-slate-400 text-sm mt-2">
                      We sent a verification code to your email
                    </p>
                  </div>
                );
              },
            },
            ResetPassword: {
              Header() {
                return (
                  <div className="text-center pt-6 pb-2 px-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-400/10 border border-amber-400/20 mb-4">
                      <svg className="w-8 h-8 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-bold text-white">Reset your password</h2>
                    <p className="text-slate-400 text-sm mt-2">
                      Enter your email to receive a reset code
                    </p>
                  </div>
                );
              },
            },
          }}
        >
          {({ signOut, user }) => (
            <DashboardContent user={user} signOut={signOut} />
          )}
        </Authenticator>
      </div>
    </div>
  );
}
